// components/PublicDashboard.jsx - Public Read-Only Dashboard View (Password Protected)
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import chartiqLogo from '../chartiq-logo.png';
import sutherlandLogo from '../output-onlinepngtools__1_-removebg-preview.png';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    RadialLinearScale,
    Filler,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Scatter, Bubble, Radar, PolarArea } from 'react-chartjs-2';
import { Layout, Eye, BarChart3, ToggleLeft, ToggleRight, Copy } from 'lucide-react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import html2canvas from 'html2canvas';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Utility: sha256 hash in browser (for password token)
async function sha256(message) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const ResponsiveGridLayout = WidthProvider(GridLayout);

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    RadialLinearScale,
    Filler,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');
if (isProd && !API_BASE) {
    throw new Error('REACT_APP_API_BASE must be set in production');
}

const colorPresets = {
    professional: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'],
};

const PublicDashboard = ({ dashboardId: propDashboardId }) => {
    const params = useParams();
    const dashboardId = propDashboardId || params.shareId;
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(false); // starts false - shows password screen first
    const [error, setError] = useState(null);
    const [layout, setLayout] = useState([]);

    // Password gate state
    const [needsPassword, setNeedsPassword] = useState(null); // null=checking, true=needs, false=no
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
    const [passwordHash, setPasswordHash] = useState(null); // stored after verify
    const passwordInputRef = useRef(null);

    const [dataLabelsMap, setDataLabelsMap] = useState(() => {
        try { const stored = localStorage.getItem('dataLabelsMap'); return stored ? JSON.parse(stored) : {}; } catch { return {}; }
    });

    const toggleDataLabel = (chartId) => {
        setDataLabelsMap(prev => {
            const isCurrentlyEnabled = prev[chartId] !== false;
            const updated = { ...prev, [chartId]: !isCurrentlyEnabled };
            try { localStorage.setItem('dataLabelsMap', JSON.stringify(updated)); } catch { }
            return updated;
        });
    };

    const handleCopyImage = async (e, chart) => {
        if (e) e.stopPropagation();
        const targetElement = e.currentTarget.closest('.dashboard-chart-card-premium');
        if (!targetElement) return;

        try {
            const canvas = await html2canvas(targetElement, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (element) => element.tagName === 'BUTTON' || element.classList.contains('drag-handle')
            });

            canvas.toBlob(blob => {
                if (!blob) return;
                const item = new ClipboardItem({ 'image/png': blob });
                navigator.clipboard.write([item]).then(() => {
                    alert('Image copied to clipboard!');
                }).catch(err => console.error('Copy failed:', err));
            });
        } catch (error) {
            console.error('Error copying image:', error);
        }
    };

    // On mount: check if password required AND load dashboard if not protected
    useEffect(() => {
        checkPasswordRequired();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId]);

    // After password verified → load dashboard with hash token
    useEffect(() => {
        if (passwordHash) {
            loadPublicDashboard(passwordHash);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passwordHash]);

    // Check if password required. If not protected → load dashboard directly from here.
    const checkPasswordRequired = async () => {
        setNeedsPassword(null); // show spinner
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/public/dashboards/${dashboardId}`);
            if (res.status === 401) {
                const data = await res.json();
                if (data.passwordProtected) {
                    setNeedsPassword(true);
                    setLoading(false);
                    setTimeout(() => passwordInputRef.current?.focus(), 100);
                    return;
                }
            }
            if (res.ok) {
                // No password needed — parse and set dashboard directly
                const data = await res.json();
                setNeedsPassword(false);
                setDashboard(data);
                processLayoutFromData(data);
            } else {
                const data = await res.json().catch(() => ({}));
                setError(data.error || 'Dashboard not found');
                setNeedsPassword(false);
            }
        } catch (e) {
            setError('Failed to connect to server');
            setNeedsPassword(false);
        } finally {
            setLoading(false);
        }
    };

    // Used for password-protected dashboards (called with verified hash)
    const loadPublicDashboard = async (hash) => {
        try {
            setLoading(true);
            setError(null);

            const headers = {};
            if (hash) {
                headers['x-dashboard-token'] = hash;
            }

            const res = await fetch(`${API_BASE}/public/dashboards/${dashboardId}`, { headers });

            if (!res.ok) {
                const errorData = await res.json();
                if (res.status === 401 && errorData.passwordProtected) {
                    setPasswordError('❌ Password mismatch. Try again.');
                    setPasswordHash(null);
                    setNeedsPassword(true);
                    return;
                }
                throw new Error(errorData.error || 'Dashboard not found');
            }

            const data = await res.json();
            setDashboard(data);
            setNeedsPassword(false);
            processLayoutFromData(data);
        } catch (err) {
            console.error('Error loading public dashboard:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Shared layout calculation logic
    const processLayoutFromData = (data) => {
        if (data.charts && data.charts.length > 0) {
            let initialLayout = [];
            const currentChartIds = data.charts.map(c => c.id.toString());

            let dashboardLayout = data.layout;
            if (typeof dashboardLayout === 'string') {
                try { dashboardLayout = JSON.parse(dashboardLayout); } catch (e) { dashboardLayout = null; }
            }

            if (dashboardLayout && Array.isArray(dashboardLayout) && dashboardLayout.length > 0) {
                initialLayout = dashboardLayout.filter(l => currentChartIds.includes(l.i));
                const existingLayoutIds = initialLayout.map(l => l.i);
                const newCharts = data.charts.filter(c => !existingLayoutIds.includes(c.id.toString()));
                newCharts.forEach((chart, i) => {
                    initialLayout.push({ i: chart.id.toString(), x: (existingLayoutIds.length + i) % 2 * 6, y: Infinity, w: 6, h: 10, minW: 3, minH: 6 });
                });
            } else {
                initialLayout = data.charts.map((chart, i) => ({
                    i: chart.id.toString(), x: (i % 2) * 6, y: Math.floor(i / 2) * 10, w: 6, h: 10, minW: 3, minH: 6
                }));
            }
            setLayout(initialLayout);

            if (initialLayout.length > 0) {
                const sortedCharts = [...data.charts].sort((a, b) => {
                    const la = initialLayout.find(l => l.i === a.id.toString());
                    const lb = initialLayout.find(l => l.i === b.id.toString());
                    if (la && lb) { if (la.y !== lb.y) return la.y - lb.y; return la.x - lb.x; }
                    return 0;
                });
                setDashboard(prev => ({ ...prev, charts: sortedCharts }));
            }
        }
    };

    // Submit password
    const handlePasswordSubmit = async (e) => {
        e?.preventDefault();
        if (!passwordInput.trim()) {
            setPasswordError('Please enter the password');
            return;
        }
        setIsVerifyingPassword(true);
        setPasswordError('');
        try {
            const hash = await sha256(passwordInput.trim());
            const res = await fetch(`${API_BASE}/public/dashboards/${dashboardId}/verify-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: passwordInput.trim() })
            });
            const data = await res.json();
            if (data.verified) {
                setPasswordHash(hash); // triggers useEffect → loadPublicDashboard(hash)
                setPasswordError('');
            } else {
                setPasswordError('❌ Incorrect password. Please try again.');
            }
        } catch (e) {
            setPasswordError('Failed to verify password');
        } finally {
            setIsVerifyingPassword(false);
        }
    };



    // --- HELPERS ---
    const findMatchingKey = (dataRow, key) => {
        if (!dataRow || !key) return null;
        if (dataRow.hasOwnProperty(key)) return key;
        const lowerKey = key.toLowerCase();
        const match = Object.keys(dataRow).find(k => k.toLowerCase() === lowerKey);
        if (match) return match;
        const partial = Object.keys(dataRow).find(k => k.toLowerCase().includes(lowerKey) || lowerKey.includes(k.toLowerCase()));
        return partial || null;
    };

    const isMostlyNumeric = (arr) => {
        if (!arr || arr.length === 0) return false;
        const numericCount = arr.filter(v => v !== null && v !== undefined && v !== '' && !isNaN(parseFloat(v)) && isFinite(v)).length;
        return numericCount >= arr.length * 0.7;
    };

    const parseNumeric = (val) => {
        if (val === null || val === undefined) return 0;
        if (typeof val === 'number') return val;
        const cleaned = val.toString().replace(/,/g, '').trim();
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    };

    const renderChart = (chart) => {
        const rawChartType = chart.chart_type || 'bar';
        // Normalize chart type: DB may store lowercase (e.g. 'horizontalbar')
        const normalizeChartType = (t) => {
            const map = {
                'horizontalbar': 'horizontalBar',
                'stackedbar': 'stackedBar',
                'groupedbar': 'groupedBar',
                'multiline': 'multiLine',
                'stackedarea': 'stackedArea',
                'polararea': 'polarArea',
            };
            return map[String(t).toLowerCase().trim()] || String(t).trim();
        };
        const chartType = normalizeChartType(rawChartType);
        const dataMapping = chart.data_mapping || { x: 'label', y: 'value' };
        let mapping = dataMapping;
        if (typeof mapping === 'string') {
            try { mapping = JSON.parse(mapping); } catch (e) { }
        }

        // Data
        let data = chart.data || [];
        if (!data || data.length === 0) {
            return (
                <div className="no-data-preview">
                    <BarChart3 size={32} opacity={0.3} />
                    <span>No data available</span>
                </div>
            );
        }

        // --- SMART MAPPING LOGIC (MATCHING APP.JSX) ---
        const sampleRow = data[0];
        const resolvedX = findMatchingKey(sampleRow, mapping.x);
        const resolvedY = findMatchingKey(sampleRow, mapping.y);

        if (resolvedX && resolvedX !== mapping.x) mapping = { ...mapping, x: resolvedX };
        if (resolvedY && resolvedY !== mapping.y) mapping = { ...mapping, y: resolvedY };

        // Auto-swap detection
        if (!['scatter', 'bubble'].includes(chartType)) {
            const sampleX = data.slice(0, 5).map(row => row[mapping.x]);
            const sampleY = data.slice(0, 5).map(row => row[mapping.y]);
            if (isMostlyNumeric(sampleX) && !isMostlyNumeric(sampleY)) {
                mapping = { ...mapping, x: mapping.y, y: mapping.x };
            }
        }

        // Colors
        const chartColors = (chart.colors && chart.colors.length > 0)
            ? chart.colors
            : colorPresets.professional;

        // Exact labels and values extraction from App.jsx
        const labels = data.map(item => {
            const val = (item[mapping.x] ?? item[Object.keys(item).find(k => k.toLowerCase() === (mapping.x || '').toLowerCase())]);
            return val || '';
        });
        const values = data.map(item => {
            const val = (item[mapping.y] ?? item[Object.keys(item).find(k => k.toLowerCase() === (mapping.y || '').toLowerCase())]);
            return parseNumeric(val);
        });

        // Generate colors
        const generateColors = (count, opacity = '80') => {
            return Array.from({ length: count }, (_, index) => {
                const color = chartColors[index % chartColors.length];
                return ['pie', 'donut', 'radar', 'polarArea'].includes(chartType) ? color : color + opacity;
            });
        }

        const backgroundColors = generateColors(labels.length, '80');
        const borderColors = generateColors(labels.length, 'FF');


        const themeGridColor = 'rgba(100, 116, 139, 0.1)';
        const themeTextColor = '#64748b';

        const baseOptions = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: { top: 10, right: 20, bottom: 10, left: 10 }
            },
            plugins: {
                legend: {
                    display: ['pie', 'donut', 'radar', 'polarArea', 'multiLine', 'stackedBar', 'groupedBar'].includes(chartType),
                    position: 'bottom',
                    labels: {
                        color: themeTextColor,
                        usePointStyle: true,
                        boxWidth: 8,
                        padding: 20,
                        font: { size: 11, family: "'Inter', 'Segoe UI', sans-serif" }
                    }
                },
                datalabels: {
                    display: function (context) {
                        return chart.id ? (dataLabelsMap[chart.id] !== false) : true;
                    },
                    color: themeTextColor,
                    align: 'end',
                    anchor: 'end',
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    borderRadius: 4,
                    padding: 3,
                    font: { weight: 'bold', size: 10 },
                    formatter: (value) => {
                        const numVal = Number(value);
                        if (!isNaN(numVal) && value !== null && value !== '') {
                            if (Math.abs(numVal) >= 1000000) return (numVal / 1000000).toFixed(1) + 'M';
                            if (Math.abs(numVal) >= 1000) return (numVal / 1000).toFixed(1) + 'K';
                            if (numVal % 1 !== 0) return numVal.toFixed(2);
                            return numVal;
                        }
                        return value;
                    }
                },
                title: { display: false }, // Title is handled by card header
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#0f172a',
                    bodyColor: '#475569',
                    borderColor: 'rgba(0,0,0,0.1)',
                    borderWidth: 1,
                    padding: 12,
                    cornerRadius: 8,
                    titleFont: { size: 13, family: "'Inter', sans-serif", weight: '600' },
                    bodyFont: { size: 12, family: "'Inter', sans-serif" },
                    displayColors: true,
                    boxPadding: 4,
                    usePointStyle: true
                }
            }
        };

        const scaleOptions = {
            x: {
                ticks: { color: themeTextColor, font: { size: 10 }, maxRotation: 45, minRotation: 0 },
                grid: { color: themeGridColor, display: false },
                beginAtZero: true
            },
            y: {
                ticks: { color: themeTextColor, font: { size: 10 } },
                grid: { color: themeGridColor, borderDash: [4, 4] },
                beginAtZero: true
            }
        };

        let chartConfig;
        let ChartComponent = Bar;
        let options = { ...baseOptions };

        switch (chartType) {
            case 'bar':
            case 'horizontalBar':
                ChartComponent = Bar;
                chartConfig = {
                    labels,
                    datasets: [{
                        label: mapping.y,
                        data: values,
                        backgroundColor: backgroundColors,
                        borderColor: borderColors,
                        borderWidth: 2
                    }]
                };
                options.indexAxis = chartType === 'horizontalBar' ? 'y' : 'x';
                options.scales = chartType === 'horizontalBar' ? {
                    x: {
                        // VALUE axis (numeric)
                        type: 'linear',
                        ticks: { color: themeTextColor, font: { size: 10 } },
                        grid: { color: themeGridColor },
                        beginAtZero: true,
                        min: 0
                    },
                    y: {
                        // CATEGORY axis (labels)
                        type: 'category',
                        ticks: { color: themeTextColor, font: { size: 10 } },
                        grid: { color: themeGridColor, display: false }
                    }
                } : {
                    x: {
                        // CATEGORY axis (labels) for normal bar
                        type: 'category',
                        ticks: { color: themeTextColor, font: { size: 10 }, maxRotation: 45, minRotation: 0 },
                        grid: { color: themeGridColor, display: false }
                    },
                    y: {
                        // VALUE axis (numeric) for normal bar
                        type: 'linear',
                        ticks: { color: themeTextColor, font: { size: 10 } },
                        grid: { color: themeGridColor, borderDash: [4, 4] },
                        beginAtZero: true
                    }
                };
                break;

            case 'stackedBar':
            case 'groupedBar':
                ChartComponent = Bar;
                const series = mapping.series ? [...new Set(data.map(item => item[mapping.series]))] : null;
                if (series && series.length > 1) {
                    const uniqueLabels = [...new Set(labels)];
                    const datasets = series.map((s, idx) => ({
                        label: s,
                        data: uniqueLabels.map(label => {
                            const item = data.find(d => d[mapping.x] === label && d[mapping.series] === s);
                            return item ? parseFloat(item[mapping.y]) || 0 : 0;
                        }),
                        backgroundColor: chartColors[idx % chartColors.length] + '80',
                        borderColor: chartColors[idx % chartColors.length],
                        borderWidth: 2
                    }));
                    chartConfig = { labels: uniqueLabels, datasets };
                } else {
                    chartConfig = {
                        labels,
                        datasets: [{
                            label: mapping.y,
                            data: values,
                            backgroundColor: backgroundColors,
                            borderColor: borderColors,
                            borderWidth: 2
                        }]
                    };
                }
                options.scales = {
                    x: { ...scaleOptions.x, type: 'category', stacked: chartType === 'stackedBar' },
                    y: { ...scaleOptions.y, type: 'linear', stacked: chartType === 'stackedBar' }
                };
                break;

            case 'line':
            case 'multiLine':
            case 'area':
            case 'stackedArea':
                ChartComponent = Line;
                const lineSeries = mapping.series ? [...new Set(data.map(item => item[mapping.series]))] : null;

                if (['multiLine', 'stackedArea'].includes(chartType) && lineSeries && lineSeries.length > 1) {
                    const uniqueLineLabels = [...new Set(labels)];
                    const datasets = lineSeries.map((s, idx) => ({
                        label: s,
                        data: uniqueLineLabels.map(label => {
                            const item = data.find(d => d[mapping.x] === label && d[mapping.series] === s);
                            return item ? parseFloat(item[mapping.y]) || 0 : 0;
                        }),
                        borderColor: chartColors[idx % chartColors.length],
                        backgroundColor: chartColors[idx % chartColors.length] + (chartType.includes('Area') ? '80' : '20'),
                        borderWidth: 2,
                        tension: 0.4,
                        fill: chartType.includes('Area')
                    }));
                    chartConfig = { labels: uniqueLineLabels, datasets };
                } else {
                    chartConfig = {
                        labels,
                        datasets: [{
                            label: mapping.y,
                            data: values,
                            borderColor: chartColors[0],
                            backgroundColor: chartColors[0] + (chartType.includes('area') ? '80' : '20'),
                            borderWidth: 2,
                            tension: 0.4,
                            fill: chartType.includes('area') || chartType === 'stackedArea'
                        }]
                    };
                }
                options.scales = {
                    x: {
                        ticks: { color: themeTextColor, font: { size: 10 }, maxRotation: 45, minRotation: 0 },
                        grid: { color: themeGridColor, display: false },
                        stacked: chartType === 'stackedArea'
                    },
                    y: {
                        ticks: { color: themeTextColor, font: { size: 10 } },
                        grid: { color: themeGridColor, borderDash: [4, 4] },
                        stacked: chartType === 'stackedArea'
                    }
                };
                break;

            case 'pie':
                ChartComponent = Pie;
                chartConfig = {
                    labels,
                    datasets: [{
                        data: values,
                        backgroundColor: backgroundColors,
                        borderColor: borderColors,
                        borderWidth: 2
                    }]
                };
                break;

            case 'donut':
                ChartComponent = Doughnut;
                chartConfig = {
                    labels,
                    datasets: [{
                        data: values,
                        backgroundColor: backgroundColors,
                        borderColor: borderColors,
                        borderWidth: 2
                    }]
                };
                options.cutout = '60%';
                break;

            case 'radar':
                ChartComponent = Radar;
                chartConfig = {
                    labels,
                    datasets: [{
                        label: mapping.y,
                        data: values,
                        backgroundColor: chartColors[0] + '40',
                        borderColor: chartColors[0],
                        borderWidth: 2,
                        pointBackgroundColor: chartColors[0]
                    }]
                };
                options = {
                    ...baseOptions,
                    scales: {
                        r: {
                            angleLines: { color: themeGridColor },
                            grid: { color: themeGridColor, borderDash: [4, 4] },
                            pointLabels: { color: themeTextColor, font: { size: 11 } },
                            ticks: {
                                backdropColor: 'transparent',
                                color: themeTextColor,
                                font: { size: 10 }
                            }
                        }
                    }
                };
                break;

            case 'polarArea':
                ChartComponent = PolarArea;
                chartConfig = {
                    labels,
                    datasets: [{
                        label: mapping.y,
                        data: values,
                        backgroundColor: backgroundColors,
                        borderColor: borderColors,
                        borderWidth: 2
                    }]
                };
                options = {
                    ...baseOptions,
                    scales: {
                        r: {
                            grid: { color: themeGridColor, borderDash: [4, 4] },
                            ticks: { backdropColor: 'transparent', color: themeTextColor, font: { size: 10 } }
                        }
                    }
                };
                break;

            case 'scatter':
                ChartComponent = Scatter;
                chartConfig = {
                    datasets: [{
                        label: `${mapping.x} vs ${mapping.y}`,
                        data: data.map(item => ({
                            x: parseFloat(item[mapping.x]) || 0,
                            y: parseFloat(item[mapping.y]) || 0
                        })),
                        backgroundColor: chartColors[0] + '80',
                        borderColor: chartColors[0],
                        borderWidth: 2,
                        pointRadius: 6
                    }]
                };
                options.scales = scaleOptions;
                break;

            case 'bubble':
                ChartComponent = Bubble;
                const sizeColumn = mapping.size || mapping.y;
                const sizeValues = data.map(item => Math.abs(parseFloat(item[sizeColumn])) || 1);
                const maxSize = Math.max(...sizeValues);
                const minSize = Math.min(...sizeValues);
                const sizeRange = maxSize - minSize || 1;

                chartConfig = {
                    datasets: [{
                        label: `${mapping.x} vs ${mapping.y}`,
                        data: data.map((item, index) => ({
                            x: isNaN(parseFloat(item[mapping.x])) ? index : parseFloat(item[mapping.x]),
                            y: parseFloat(item[mapping.y]) || 0,
                            r: 5 + ((Math.abs(parseFloat(item[sizeColumn])) - minSize) / sizeRange) * 25,
                        })),
                        backgroundColor: chartColors[0] + '60',
                        borderColor: chartColors[0],
                        borderWidth: 2
                    }]
                };
                options.scales = scaleOptions;
                break;

            default:
                ChartComponent = Bar;
                chartConfig = {
                    labels,
                    datasets: [{
                        label: mapping.y,
                        data: values,
                        backgroundColor: backgroundColors,
                        borderColor: borderColors,
                        borderWidth: 2
                    }]
                };
                options.scales = scaleOptions;
        }

        return <ChartComponent data={chartConfig} options={options} />;
    };

    // ====== RENDER STATES ======

    // Checking if password is required
    if (needsPassword === null) {
        return (
            <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                    <div style={{ width: 48, height: 48, border: '3px solid #6366f1', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ fontSize: '1rem' }}>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // PASSWORD GATE SCREEN
    if (needsPassword === true && !passwordHash) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
                padding: '20px'
            }}>
                {/* Animated background orbs */}
                <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                    <div style={{ position: 'absolute', top: '20%', left: '15%', width: 300, height: 300, background: 'radial-gradient(circle, rgba(99,102,241,0.15), transparent 70%)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite' }} />
                    <div style={{ position: 'absolute', bottom: '20%', right: '15%', width: 250, height: 250, background: 'radial-gradient(circle, rgba(139,92,246,0.12), transparent 70%)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite 2s' }} />
                </div>

                <div style={{
                    background: 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(99,102,241,0.3)',
                    borderRadius: 24,
                    padding: '48px 40px',
                    width: 420,
                    maxWidth: '95vw',
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
                }}>
                    {/* Lock icon */}
                    <div style={{ textAlign: 'center', marginBottom: 32 }}>
                        <div style={{
                            width: 80, height: 80,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 0 40px rgba(99,102,241,0.5)'
                        }}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h1 style={{ color: '#f1f5f9', fontSize: '1.6rem', fontWeight: 800, margin: '0 0 8px' }}>
                            Protected Dashboard
                        </h1>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', margin: 0, lineHeight: 1.5 }}>
                            This dashboard is password protected.<br />
                            Enter the password to view it.
                        </p>
                    </div>

                    <form onSubmit={handlePasswordSubmit}>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ color: '#94a3b8', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 10 }}>
                                🔑 Access Password
                            </label>
                            <input
                                ref={passwordInputRef}
                                type="text"
                                value={passwordInput}
                                onChange={e => { setPasswordInput(e.target.value); setPasswordError(''); }}
                                onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()}
                                placeholder="XXXX-XXXX-XXXX"
                                autoComplete="off"
                                style={{
                                    width: '100%',
                                    padding: '14px 18px',
                                    background: 'rgba(255,255,255,0.06)',
                                    border: `1px solid ${passwordError ? '#ef4444' : 'rgba(99,102,241,0.4)'}`,
                                    borderRadius: 12,
                                    color: '#f1f5f9',
                                    fontSize: '1.3rem',
                                    fontFamily: 'monospace',
                                    letterSpacing: '0.15em',
                                    textAlign: 'center',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    transition: 'border-color 0.2s',
                                    textTransform: 'uppercase'
                                }}
                                onFocus={e => { if (!passwordError) e.target.style.borderColor = '#6366f1'; }}
                                onBlur={e => { if (!passwordError) e.target.style.borderColor = 'rgba(99,102,241,0.4)'; }}
                            />
                            {passwordError && (
                                <p style={{ color: '#f87171', fontSize: '0.85rem', margin: '8px 0 0', textAlign: 'center' }}>
                                    {passwordError}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isVerifyingPassword || !passwordInput.trim()}
                            style={{
                                width: '100%',
                                padding: '14px',
                                background: isVerifyingPassword ? 'rgba(99,102,241,0.5)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                border: 'none',
                                borderRadius: 12,
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '1rem',
                                cursor: isVerifyingPassword ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                boxShadow: '0 4px 15px rgba(99,102,241,0.4)',
                                transition: 'all 0.2s',
                                opacity: !passwordInput.trim() ? 0.5 : 1
                            }}
                        >
                            {isVerifyingPassword ? (
                                <>
                                    <div style={{ width: 18, height: 18, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                                    Verifying...
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M8 11V6a4 4 0 0 1 8 0v5" /><rect x="3" y="11" width="18" height="11" rx="2" /></svg>
                                    Unlock Dashboard
                                </>
                            )}
                        </button>
                    </form>

                    <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'center', margin: '20px 0 0', lineHeight: 1.5 }}>
                        🔒 Password provided by the dashboard owner.<br />Contact them if you need access.
                    </p>
                </div>

                <style>{`
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse { 0%,100%{ transform: scale(1); opacity:0.6; } 50%{ transform: scale(1.05); opacity:1; } }
                `}</style>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="public-dashboard-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // Guard: dashboard not yet loaded (race condition between state updates)
    if (!dashboard && !error) {
        return (
            <div className="public-dashboard-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-dashboard-container">
                <div className="error-state">
                    <Eye size={64} opacity={0.3} />
                    <h2>Dashboard Not Available</h2>
                    <p>{error}</p>
                    <p className="hint">This dashboard may have been unpublished or doesn't exist.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="public-dashboard-container" style={{ padding: '6px 20px 20px', background: '#f8fafc', minHeight: '100vh', width: '100%', overflowY: 'auto' }}>
            {/* Slim header: ChartIQ logo left | Dashboard title center | Sutherland logo right */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '2px',
                padding: '4px 0'
            }}>
                {/* Left: ChartIQ Logo */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src={chartiqLogo}
                        alt="ChartIQ"
                        style={{ height: '44px', objectFit: 'contain' }}
                    />
                </div>

                {/* Center: Dashboard Title only */}
                <h1 style={{
                    margin: 0,
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    textAlign: 'center',
                    flex: 1,
                    padding: '0 16px'
                }}>
                    {dashboard.title}
                </h1>

                {/* Right: Sutherland Logo */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src={sutherlandLogo}
                        alt="Sutherland"
                        style={{ height: '56px', objectFit: 'contain' }}
                    />
                </div>
            </div>

            <ResponsiveGridLayout
                className="layout"
                layout={layout}
                cols={12}
                rowHeight={30}
                margin={[16, 16]}
                isDraggable={false}
                isResizable={false}
                useCSSTransforms={false}
                compactType={null}
                preventCollision={false}
            >
                {dashboard.charts && dashboard.charts.map(chart => (
                    <div key={chart.id.toString()} className="dashboard-chart-card-premium" style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>{chart.title}</h3>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} onMouseDown={e => e.stopPropagation()}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); toggleDataLabel(chart.id); }}
                                    title={dataLabelsMap[chart.id] !== false ? "Hide Data Labels" : "Show Data Labels"}
                                    style={{ width: '46px', height: '30px', padding: '0 8px', borderRadius: '15px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                    {dataLabelsMap[chart.id] !== false ? <ToggleRight size={22} style={{ color: '#3b82f6' }} /> : <ToggleLeft size={22} color="#64748b" />}
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleCopyImage(e, chart); }}
                                    title="Copy Chart Image"
                                    style={{ width: '30px', height: '30px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', cursor: 'pointer' }}
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                        </div>
                        <div style={{ flex: 1, position: 'relative', padding: '16px', overflow: 'hidden' }}>
                            {renderChart(chart)}
                        </div>
                    </div>
                ))}
            </ResponsiveGridLayout>

            <div className="public-dashboard-footer" style={{ marginTop: '40px', textAlign: 'center', color: '#94a3b8', padding: '20px', borderTop: '1px solid #e2e8f0' }}>
                <p>Powered by <strong>ChartIQ</strong> • Published on {new Date(dashboard.published_at || Date.now()).toLocaleDateString()}</p>
            </div>
        </div>
    );
};

export default PublicDashboard;
